// This module provides a utility for logging debug messages in a React application.
import { useCallback, useRef } from 'react';
import debug from 'debug';

// Create a debug instance for logging
const log = debug('app:debug');

function enableDebugLogging() {
  // Enable debug logging for the app
  if (process.env.REACT_APP_ENV === 'development') {
    // It sets the debug storage to 'app:debug' to filter logs
    localStorage.setItem('debug', 'app:debug');
  }
}

export const useVerboseLog = () => {
  // Keep the latest logged message per component
  const cacheRef = useRef<string | null>(null);

  // Function to safely stringify objects, handling circular references and BigInt
  const safeStringify = (value: unknown): string => {
    const seen = new WeakSet();

    try {
      return JSON.stringify(value, (_, val) => {
        // Handle BigInt
        if (typeof val === 'bigint') {
          return val.toString();
        }

        // Handle circular references
        if (typeof val === 'object' && val !== null) {
          if (seen.has(val)) return '[Circular]';
          seen.add(val);
        }

        return val;
      });
    } catch (err) {    
      // Handle unserializable values. This is a fallback for cases where JSON.stringify fails  
      return `'<<Unserializable>>', error: ${err}`;
    }
  };

  const logDebug = useCallback((componentName: string, message: unknown) => {
    // Check if the environment is development. If not, return early.
    if (process.env.REACT_APP_ENV !== 'development') return;

    // Enable debug logging for the app if not already enabled.
    if(localStorage.getItem('debug') !== 'app:debug') enableDebugLogging();

    // Convert the message to a string for comparison
    // This is necessary because the message can be an object or any other type
    const newMessage = safeStringify(message);

    // Check if the message is different from the last logged message for this component
    // This prevents logging the same message multiple times
    if (cacheRef.current === newMessage) return;
    // Update the cache
    cacheRef.current = newMessage;

    // If the new message is different from the last message, log it
    log(componentName, message);
  }, []);

  return logDebug;
};
